import { Box, Link, Typography } from '@mui/material';
import { numericSx } from '../../../theme/typography';
import { useCurrency } from '../../../contexts/CurrencyContext';
import { compactCurrency, niceCeiling } from '../../reports/panels/moneyChartAxis';
import { PanelCard } from './PanelCard';
import type { RevenueByMonthPoint, RevenueByMonthWidget } from '../types';

interface RevenueByMonthChartProps {
  data: RevenueByMonthWidget | undefined;
  loading?: boolean;
  onOpenReports?: () => void;
}

const CHART_HEIGHT = 180;
const Y_AXIS_WIDTH = 48;
const X_AXIS_HEIGHT = 22;
const Y_TICK_COUNT = 3; // top / midpoint / baseline

/**
 * 12-month vertical bar chart with a Y-axis tick column on the left and
 * a row of month labels along the bottom. Pure CSS render so the visuals
 * match the prototype's minimal aesthetic exactly (MUI X BarChart's tick
 * formatting and axis paddings would diverge).
 *
 * Reads from `widgets.revenueByMonth` (planner+). Hidden via the section
 * gate when Financials chip is off — the panel itself doesn't gate on
 * permissions.
 */
export function RevenueByMonthChart({
  data,
  loading,
  onOpenReports,
}: RevenueByMonthChartProps) {
  const { formatCurrency } = useCurrency();
  const points = data?.points ?? [];
  const target = data?.targetMonthly ?? null;
  // `revenue` is gated on `view:project:value`, so for a caller without that
  // key every point arrives labelled but figureless. There are still twelve of
  // them, so the zero-pad below does not fire and the undefined reached
  // `Math.max` — three `£NaN` axis ticks and a `height: NaN%` on every bar.
  // A withheld series renders as an absence, not as zeros: a flat £0 chart is
  // a confident claim that the year earned nothing.
  const figuresWithheld =
    points.length > 0 && points.every((p) => typeof p.revenue !== 'number');
  const readablePoints = points.filter(
    (p): p is RevenueByMonthPoint & { revenue: number } =>
      typeof p.revenue === 'number' && Number.isFinite(p.revenue),
  );
  const rawMax = Math.max(
    ...readablePoints.map((p) => p.revenue),
    target ?? 0,
    1,
  );
  // Round the axis ceiling up to a "nice" number so the Y-tick labels
  // read cleanly (e.g. £4M instead of £3.847M).
  const yMax = niceCeiling(rawMax);
  // Top → middle → 0 (for vertical positioning of the tick labels).
  const yTickValues = Array.from(
    { length: Y_TICK_COUNT },
    (_, i) => yMax * (1 - i / (Y_TICK_COUNT - 1)),
  );

  // Render-time pad — used when no data has arrived yet so the chart
  // still occupies its slot with empty bar placeholders.
  const renderPoints = readablePoints.length
    ? readablePoints
    : Array.from({ length: 12 }, () => ({
        monthISO: '',
        label: '',
        revenue: 0,
      }));

  return (
    <PanelCard
      title="Revenue by month"
      action={
        onOpenReports ? (
          <Link
            component="button"
            type="button"
            onClick={onOpenReports}
            underline="hover"
            sx={{ fontSize: '0.8125rem' }}
          >
            Open reports →
          </Link>
        ) : undefined
      }
      data-testid="widget-revenue-by-month"
    >
      {figuresWithheld ? (
        <Typography
          variant="body2"
          sx={{ color: 'text.secondary', py: 3, textAlign: 'center' }}
        >
          Revenue figures are not available to your role.
        </Typography>
      ) : (
      <>
      <Box
        sx={{
          display: 'flex',
          gap: 2.25,
          mb: 1.75,
          fontSize: '0.71875rem',
        }}
      >
        <LegendSwatch label="Revenue" colorKey="primary" />
        {target != null && target > 0 ? (
          <LegendSwatch
            label={`Target ${compactCurrency(target, formatCurrency)}`}
            colorKey="line"
          />
        ) : null}
      </Box>

      {/* 2-column · 2-row grid:
          col 1 = Y-axis labels (48px) · col 2 = plot area (1fr)
          row 1 = chart height (180px) · row 2 = X-axis month labels (22px) */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: `${Y_AXIS_WIDTH}px 1fr`,
          gridTemplateRows: `${CHART_HEIGHT}px ${X_AXIS_HEIGHT}px`,
          columnGap: 1,
        }}
      >
        {/* Y-axis tick column */}
        <Box sx={{ position: 'relative' }}>
          {yTickValues.map((v, i) => (
            <Typography
              key={i}
              component="div"
              sx={[
                numericSx,
                {
                  position: 'absolute',
                  right: 0,
                  // Top of label is offset so the label's vertical centre
                  // sits on the corresponding guide line.
                  top: `calc(${(i / (Y_TICK_COUNT - 1)) * 100}% - 6px)`,
                  fontSize: '0.625rem',
                  color: 'text.disabled',
                  lineHeight: 1,
                  whiteSpace: 'nowrap',
                },
              ]}
            >
              {compactCurrency(v, formatCurrency)}
            </Typography>
          ))}
        </Box>

        {/* Plot area — guide lines via background-image (3 horizontal hairlines
            evenly spaced) so no decorative divs sit in front of the bars. */}
        <Box
          sx={(theme) => ({
            position: 'relative',
            backgroundImage: `repeating-linear-gradient(to bottom,
              ${theme.palette.divider} 0,
              ${theme.palette.divider} 1px,
              transparent 1px,
              transparent calc(${100 / (Y_TICK_COUNT - 1)}% - 0.5px))`,
            // Trailing 1px line at the very bottom (the baseline).
            borderBottom: `1px solid ${theme.palette.divider}`,
          })}
        >
          {/* Optional target line — dashed, positioned by % of yMax. */}
          {target != null && target > 0 && yMax > 0 ? (
            <Box
              sx={(theme) => ({
                position: 'absolute',
                left: 0,
                right: 0,
                top: `${100 - (target / yMax) * 100}%`,
                height: '0px',
                borderTop: `1px dashed ${theme.palette.text.disabled}`,
                opacity: 0.7,
                pointerEvents: 'none',
              })}
            />
          ) : null}
          {/* 12-column bar grid */}
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'grid',
              gridTemplateColumns: `repeat(${renderPoints.length}, 1fr)`,
              columnGap: '6px',
              alignItems: 'end',
              px: '4px',
            }}
          >
            {renderPoints.map((p, i) => {
              const heightPct = yMax > 0 ? (p.revenue / yMax) * 100 : 0;
              return (
                <Box
                  key={i}
                  sx={(theme) => ({
                    height: `${heightPct}%`,
                    minHeight: p.revenue > 0 ? '4px' : 0,
                    backgroundImage: p.revenue
                      ? `linear-gradient(180deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`
                      : 'none',
                    borderRadius: '4px 4px 0 0',
                    opacity: loading ? 0.4 : 1,
                  })}
                  title={
                    p.label
                      ? `${p.label}: ${formatCurrency(p.revenue)}`
                      : undefined
                  }
                />
              );
            })}
          </Box>
        </Box>

        {/* row 2 col 1 — empty spacer under the Y-axis column */}
        <Box />

        {/* X-axis row — month labels aligned 1:1 with the bar grid */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: `repeat(${renderPoints.length}, 1fr)`,
            columnGap: '6px',
            alignItems: 'start',
            px: '4px',
            pt: '6px',
          }}
        >
          {renderPoints.map((p, i) => (
            <Typography
              key={i}
              component="div"
              sx={[
                numericSx,
                {
                  fontSize: '0.6875rem',
                  color: 'text.disabled',
                  textAlign: 'center',
                  lineHeight: 1.2,
                },
              ]}
            >
              {p.label}
            </Typography>
          ))}
        </Box>
      </Box>
      </>
      )}
    </PanelCard>
  );
}

function LegendSwatch({
  label,
  colorKey,
}: {
  label: string;
  colorKey: 'primary' | 'line';
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        color: 'text.secondary',
      }}
    >
      <Box
        sx={(theme) => ({
          width: 10,
          height: colorKey === 'primary' ? 10 : 2,
          backgroundColor:
            colorKey === 'primary'
              ? theme.palette.primary.main
              : theme.palette.text.disabled,
          borderRadius: 0.5,
        })}
      />
      {label}
    </Box>
  );
}
